import { createAction, handleActions, Action } from 'redux-actions';
import { useRedux } from 'util/hook/redux';
import { Dispatch } from 'redux';
import history from 'store/history';

import { api } from 'util/api';
import {
	STORAGE_KEY,
	StorageType,
	setItemToStorage,
	removeItemInStorage,
} from 'util/hook/useStorage';
import { HospitalDepartmentResource, HospitalBranchResource, V1HospitalBrancheDepartmentRefListRequestParams, HospitalBrancheDepartmentRefResource, V1UserSingleFirstDiagnosisListRequestParams } from 'util/api/swaggerApi/data-contracts';
import { FormControl } from 'util/formSystem/formControl';
import setupFormModel from 'util/formSystem/setupForm';
import { updateAccessToken } from './auth';

import { GetState, State as GlobalState } from './reducers';

export interface ExtendedHospitalDepartmentResource extends HospitalDepartmentResource {
    isAvailable: boolean;
};

export interface ExtendedHospitalBranchResource extends HospitalBranchResource {
		isAvailable: boolean;
		clinicsNameArray?: string[];
};

export interface SingleFirstDiagnosisInfo {
	success: boolean;
	message: string;
	data: string;
	formUrl?: string;
	paramsFormUrl?: string;
}

export interface State {
	loading: boolean;
	departmentList: ExtendedHospitalDepartmentResource[];
	branchList: ExtendedHospitalBranchResource[];
	fullBranchList: ExtendedHospitalBranchResource[];
	selectedDepartment: FormControl<ExtendedHospitalDepartmentResource>;
	selectedBranch: FormControl<ExtendedHospitalBranchResource>;
	clinicsCodeList: string[];
	singleFirstDiagnosis: SingleFirstDiagnosisInfo;
}

export const defaultState: State = {
	loading: false,
	departmentList: [],
	branchList: [],
	fullBranchList: [],
	selectedDepartment: {} as FormControl<ExtendedHospitalDepartmentResource>,
	selectedBranch: {} as FormControl<ExtendedHospitalBranchResource>,
	clinicsCodeList: [],
	singleFirstDiagnosis: {} as SingleFirstDiagnosisInfo,
};

export const formModelConfig = setupFormModel<State>('REGISTRATION_FORM', (getState: GetState) => {
	const { departmentRegistration } = getState();
	return departmentRegistration;
});

export const getDepartmentList = createAction(
	'GET_DEPARTMENT_LIST', async() => {
		const { v1HospitalDepartmentListList } = api;

		try {
			const { data } = await v1HospitalDepartmentListList();

			const extendedData = data?.data?.map((department:HospitalDepartmentResource) => ({
				...department,
				isAvailable: true, 
			}));
			return extendedData;
				
		} catch (e) {
			console.log('getDepartmentList error', e);

			return [];
		}
	},
);

const updateDepartmentList = createAction('UPDATE_DEPARTMENT_LIST', (departmentList: ExtendedHospitalDepartmentResource[]) => departmentList);


export const getBranchList = createAction(
	'GET_BRANCH_LIST', async() => {
		const { v1HospitalBrancheListList } = api;

		try {
			const { data } = await v1HospitalBrancheListList();
			const clinicsCodeList: string[] = [];

			const extendedData = data?.data?.map((branch:HospitalBranchResource) => {
				const clinicsData: string[] = [];
				branch.clinics?.forEach(clinic => {
					clinicsData.push(clinic.name || '');
					clinicsCodeList.push(clinic.code || '');
				})
				return {
					...branch,
					isAvailable: true,
					clinicsNameArray: clinicsData,
				}});
			return { branchData: extendedData, clinicsCodeList };
		} catch (e) {
			console.log('getBranchList error', e);

			return { branchData: [], clinicsCodeList: []};
		}
	},
);

const updateBranchList = createAction('UPDATE_BRANCH_LIST', (branchList: ExtendedHospitalBranchResource[]) => branchList);

const getBranchDepartmentRef = createAction(
	'GET_BRANCH_DEPARTMENT_REF', (type: 'BRANCH' | 'DEPARTMENT', id: number) =>
		async (dispatch: Dispatch, getState: GetState) => {
			const { departmentRegistration: { departmentList, fullBranchList, selectedBranch } } = getState();
			const { v1HospitalBrancheDepartmentRefList } = api;

			const params: V1HospitalBrancheDepartmentRefListRequestParams = {
				type,
				...(type === 'BRANCH' ? { branch_id: id } : { department_id: id }),
			};

			try {
				const { data } = await v1HospitalBrancheDepartmentRefList(params);
				if (type === 'DEPARTMENT') {
					const availableBranchIds = data?.data?.map((item: HospitalBrancheDepartmentRefResource) => item.id) || [];

					const newBranchList = fullBranchList.filter(branch =>
						availableBranchIds.includes(branch.id),
					);
					dispatch(updateBranchList(newBranchList));

					if (selectedBranch?.value?.id && !availableBranchIds.includes(selectedBranch.value.id)) {
						dispatch(formModelConfig.actions.setFormCtrlValue('selectedBranch', {}));
					}
				} else if ( type === 'BRANCH' && data?.data) {
					const availableDepartmentIds = data?.data[0].departments?.map((item: HospitalDepartmentResource) => item.id) || [];

					const newDepartmentList = departmentList.map(department => {
						const isAvailable = availableDepartmentIds.includes(department.id);
						return {
							...department,
							isAvailable,
						};
					})
					dispatch(updateDepartmentList(newDepartmentList));
				}

				return [];
			} catch (e) {
				console.log('getBranchDepartmentRef error', e);
				return [];
			}
		},
);

const setSelectedOptionsToLocal = createAction('SET_SELECTED_OPTIONS_TO_LOCAL', () => (_:Dispatch, getState: GetState) => {
	const { departmentRegistration: { selectedDepartment, selectedBranch } } = getState();

	setItemToStorage(STORAGE_KEY.SELECTED_DEPARTMENT, selectedDepartment.value, StorageType.LOCAL);
	setItemToStorage(STORAGE_KEY.SELECTED_BRANCH, selectedBranch.value, StorageType.LOCAL);
});

export const clearSelectedOptions = createAction('CLEAR_SELECTED_OPTIONS', () => (dispatch: Dispatch) => {
	dispatch(formModelConfig.actions.setFormCtrlValue('selectedDepartment', {}));
	dispatch(formModelConfig.actions.setFormCtrlValue('selectedBranch', {}));
	removeItemInStorage(STORAGE_KEY.SELECTED_DEPARTMENT, StorageType.LOCAL);
	removeItemInStorage(STORAGE_KEY.SELECTED_BRANCH, StorageType.LOCAL);
});

export const checkSingleFirstDiagnosis = createAction('CHECK_SINGLE_FIRST_DIAGNOSIS',(deptCode: string) => async (dispatch: Dispatch) => {
	const { v1UserSingleFirstDiagnosisList } = api;
	try {
		const response = await v1UserSingleFirstDiagnosisList({ dept: deptCode} as V1UserSingleFirstDiagnosisListRequestParams);
		const data = response?.data?.data as SingleFirstDiagnosisInfo;

		if (data?.data === 'N') {
			const paramsFormUrl = `${data.formUrl}?src=web&env=${process.env.VENDOR_FORM_ENV}`;
			data.paramsFormUrl = paramsFormUrl;
		}
		return data;

	} catch(err) {
		console.log('checkSingleFirstDiagnosis api err', err);
		const { status } = err as { status: number };
		if (status === 401) {
			dispatch(updateAccessToken(''));
			history.push('/');
		}
		return {};
	}
});

export const reducer = {
	// Workaround: HandleActions 目前定義無法支援多種 action 形式
	departmentRegistration: handleActions<State, any>( // eslint-disable-line @typescript-eslint/no-explicit-any
		{
			...formModelConfig.reducers,
			GET_DEPARTMENT_LIST_PENDING: state => ({
				...state,
				loading: true,
			}),
			GET_BRANCH_LIST_PENDING: state => ({
				...state,
				loading: true,
			}),
			GET_DEPARTMENT_LIST_FULFILLED: (
				state, 
				action: Action<ExtendedHospitalDepartmentResource[]>
			) => ({
				...state,
				departmentList: action.payload,
				loading: false,
			}),
			GET_BRANCH_LIST_FULFILLED: (
				state, 
				action: Action<{ branchData: ExtendedHospitalBranchResource[], clinicsCodeList: string[] }>
			) => ({
				...state,
				branchList: action.payload.branchData,
				fullBranchList: action.payload.branchData,
				clinicsCodeList: action.payload.clinicsCodeList,
				loading: false,
			}),
			UPDATE_DEPARTMENT_LIST: (state, action: Action<ExtendedHospitalDepartmentResource[]>) => ({
				...state,
				departmentList: action.payload,
			}),
			UPDATE_BRANCH_LIST: (state, action: Action<ExtendedHospitalBranchResource[]>) => ({
				...state,
				branchList: action.payload,
			}),
			SET_SELECTED_WEEK: (state, action) => ({
				...state,
				selectedWeek: action.payload,
			}),
			CLEAR_SELECTED_OPTIONS: state => ({
				...state,
				branchList: state.fullBranchList,
			}),
			CHECK_SINGLE_FIRST_DIAGNOSIS_FULFILLED: (state, action: Action<SingleFirstDiagnosisInfo>) => ({
				...state,
				singleFirstDiagnosis: action.payload,
			}),
			
		},
		defaultState,
	),
};

const departmentRegistrationActionsMap = {
	...formModelConfig.actions,
	getDepartmentList,
	getBranchList,
	getBranchDepartmentRef,
	updateBranchList,
	updateDepartmentList,
	setSelectedOptionsToLocal,
	clearSelectedOptions,
	checkSingleFirstDiagnosis,
};

const mapHooksToState = (state: GlobalState) => ({
	departmentList: state.departmentRegistration.departmentList,
	branchList: state.departmentRegistration.branchList,
	selectedDepartment: state.departmentRegistration.selectedDepartment,
	selectedBranch: state.departmentRegistration.selectedBranch,
	clinicsCodeList: state.departmentRegistration.clinicsCodeList,
	singleFirstDiagnosis: state.departmentRegistration.singleFirstDiagnosis,
});

type DepartmentRegistrationSelector = ReturnType<typeof mapHooksToState>;
type DepartmentRegistrationActionsMap = typeof departmentRegistrationActionsMap;

export const useDepartmentRegistration = () =>
	useRedux<DepartmentRegistrationSelector, DepartmentRegistrationActionsMap>(
		mapHooksToState,
		departmentRegistrationActionsMap,
	);
